import { Label } from '@/components/shadcn/ui/label';
import { Switch } from '@/components/shadcn/ui/switch';
import { Tooltip } from '@/shared/ui';
import useWorkspaceStore from '@/stores/WorkspaceStore';

import { useSharedLinkUpdate } from '../hooks';
import { useSharedLinkForm } from './shared-link-form-context';

const DEEPBLUEDOT_EMAIL_DOMAIN = '@deepbluedot.io';

type DisplayMember = Omit<MemberT, 'email' | 'first_name' | 'last_name'> & {
  email?: string;
  first_name?: string;
  last_name?: string;
};

function isDeepbluedotMember(member: DisplayMember) {
  return member.email
    ?.trim()
    .toLowerCase()
    .endsWith(DEEPBLUEDOT_EMAIL_DOMAIN);
}

function getMemberDisplayName(member: DisplayMember) {
  const fullName = [member.first_name, member.last_name]
    .filter((part): part is string => typeof part === 'string')
    .map((part) => part.trim())
    .filter(Boolean)
    .join(' ');

  return fullName || member.email || 'Unknown member';
}

function getDescription(
  isPublic: boolean,
  hasExpiration: boolean,
  hasPassword: boolean,
  members: DisplayMember[],
) {
  if (!isPublic) {
    return (
      <Tooltip
        content={
          <div className="flex max-h-48 min-w-48 flex-col gap-1 overflow-y-auto">
            <span className="font-semibold">Workspace members</span>
            {members.map((member) => (
              <span key={member.user_id} className="flex flex-col">
                <span>{getMemberDisplayName(member)}</span>
                {member.email && (
                  <span className="text-background/70">{member.email}</span>
                )}
              </span>
            ))}
          </div>
        }
        side="bottom"
        disabled={members.length === 0}
      >
        <span className="cursor-default underline decoration-dotted underline-offset-4">
          Private — shared with {members.length} people
        </span>
      </Tooltip>
    );
  }

  // Public enabled - build description based on settings
  const parts: string[] = ['Public — anyone can access'];

  if (hasExpiration && hasPassword) {
    parts.push('The link has an expiration date and requires a password.');
  } else if (hasExpiration) {
    parts.push('The link has an expiration date.');
  } else if (hasPassword) {
    parts.push('A password is required to access.');
  }

  return (
    <>
      {parts.map((part, index) => (
        <span key={index}>
          {part}
          {index < parts.length - 1 && <br />}
        </span>
      ))}
    </>
  );
}

export function PublicToggle() {
  const form = useSharedLinkForm();
  const { saveChanges } = useSharedLinkUpdate(form);
  const members = useWorkspaceStore((state) => state.currWorkspaceMembers);
  const sharedMembers = members.filter((member) => !isDeepbluedotMember(member));

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium text-gray-900">
          Enable public sharing
        </Label>
        <form.AppField
          name="isPublic"
          children={(field: any) => (
            <Switch
              checked={field.state.value ?? false}
              onCheckedChange={(checked) => {
                field.handleChange(checked);
                saveChanges();
              }}
            />
          )}
        />
      </div>
      <form.Subscribe
        selector={(state: any) => ({
          isPublic: state.values.isPublic,
          linkExpireEnabled: state.values.linkExpireEnabled,
          passwordEnabled: state.values.passwordEnabled,
        })}
        children={({ isPublic, linkExpireEnabled, passwordEnabled }: any) => (
          <p className="text-sm text-gray-500 leading-relaxed">
            {getDescription(
              isPublic,
              linkExpireEnabled,
              passwordEnabled,
              sharedMembers,
            )}
          </p>
        )}
      />
    </div>
  );
}
